<script lang="ts" setup>
import type { MergeAccount, OpenerType } from '~/types'

const props = defineProps<{ opener: OpenerType['openAccountMergePopup'] }>()

const { openPersonDetailsPage } = useOpeners()
const { data: popupData, close: closePopup } = props.opener

const formRef = useTemplateRef('formRef')
const {
  showUnsavedChangesModal,
  attemptClose,
  forceClose,
  cancelDiscard,
} = usePopupGuard(
  computed(() => formRef.value?.isDirty ?? false),
  closePopup,
)

const payload = computed((): MergeAccount | undefined => {
  if (!popupData.value) {
    return undefined
  }
  return {
    idToMerge: popupData.value.id,
    idToMergeInto: popupData.value.idToMergeWith ?? '',
  }
})

const { $trpc, queryClient, useMutation, makeTrpcErrorToast, makeTrpcSuccessToast } = useMutationHelpers()
const { $i18n } = useNuxtApp()
const mergeAccounts = useMutation({
  mutationFn: $trpc.accounts.mergeAccount.mutate,
  onMutate: (payload) => {
    if (payload.personIdToMergeInto) {
      return openPersonDetailsPage({ id: payload.personIdToMergeInto })
    }
    navigateTo('/users/citizens/verified')
  },
  onError: makeTrpcErrorToast({ entity: $i18n.t('account.entity'), mode: 'transfer' }),
  onSuccess: async () => {
    forceClose()

    await queryClient.invalidateQueries({ queryKey: ['accounts', 'getAll'] })
    await queryClient.invalidateQueries({ queryKey: ['accounts', 'getById'] })
    await queryClient.invalidateQueries({ queryKey: ['persons', 'getAll'] })
    await queryClient.invalidateQueries({ queryKey: ['persons', 'getById'] })
    await queryClient.invalidateQueries({ queryKey: ['cases', 'getAll'] })

    makeTrpcSuccessToast({ entity: $i18n.t('account.entity'), mode: 'transfer' })
  },
})
</script>

<template>
  <ThePopup
    v-if="payload"
    :show="Boolean(payload)"
    :title="$t('account.merge.button')"
    width="1400px"
    @close="attemptClose"
  >
    <n-spin :show="mergeAccounts.isPending.value">
      <AccountMergeForm
        ref="formRef"
        :payload
        @close="attemptClose"
        @submit="mergeAccounts.mutate"
      />
    </n-spin>
  </ThePopup>

  <TheConfirmPopup
    :show="showUnsavedChangesModal"
    :title="$t('popup.guard.title')"
    :text="$t('popup.guard.message')"
    :confirm-text="$t('popup.guard.discard')"
    :cancel-text="$t('popup.guard.cancel')"
    @close="cancelDiscard"
    @confirm="forceClose"
  />
</template>
